'use client';

import type { AIChatConfig, Web2TextConfig } from '@dealerportal/common';
import { ErrorBoundary } from 'next/dist/client/components/error-boundary';
import dynamic from 'next/dynamic';
import { createContext, useEffect, useRef, useState } from 'react';
import { ChatWindowSkeleton } from '../../../../ui-components/src/lib/skeletons/ChatWindowSkeleton';
import { AIPrompt } from '../Botpress/AIPrompt';

const FloatingContainerBody = dynamic(() => import('./FloatingContainerBody'), {
  loading: () => <ChatWindowSkeleton />,
});

// `@botpress/webchat` touches `document` at module scope, so importing it during
// SSR crashes the page render (500). Load it in the browser only.
const Botpress = dynamic(
  () => import('../Botpress/Botpress').then((m) => m.BotpressChat),
  {
    ssr: false,
    loading: () => <ChatWindowSkeleton />,
  }
);
export const BotpressContext = createContext<{
  ConversationId: string | null;
}>({
  ConversationId: null,
});
const FloatingOpenContainer = ({
  isOpen = false,
  toggleWeb2Text,
  web2TextConfig,
  aiChatConfig,
  recaptcha,
  fallbackStorePhone,
  chatBgColor,
}: {
  isOpen: boolean;
  toggleWeb2Text: () => void;
  web2TextConfig: Web2TextConfig;
  aiChatConfig: AIChatConfig;
  recaptcha: {
    Enabled: boolean;
    SiteKey?: string;
  };
  fallbackStorePhone: string;
  chatBgColor: string;
}) => {
  const [chatType, setChatType] = useState<'WEB2TEXT' | 'AI'>(() => {
    if (web2TextConfig.EnableWeb2Text) {
      return 'WEB2TEXT';
    }
    return 'AI';
  });
  useEffect(() => {
    // Keep chat type in sync after the async dealer status resolves; the
    // initial render can see EnableWeb2Text=false before the status arrives.
    setChatType(web2TextConfig.EnableWeb2Text ? 'WEB2TEXT' : 'AI');
  }, [web2TextConfig.EnableWeb2Text]);
  const [hasPromptedAI, setHasPromptedAI] = useState(false);
  const [AIConversationId, setAIConversationId] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  return (
    <ErrorBoundary
      errorComponent={(props) => (
        <div>
          <p>An error occurred during rendering</p>
          <code>{JSON.stringify(props.error)}</code>
        </div>
      )}
    >
      <BotpressContext.Provider value={{ ConversationId: AIConversationId }}>
        <div
          className={'ui-mb-3'}
          style={{
            transition:
              'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: 0,
            transform: 'scale(0)',
            transformOrigin:
              web2TextConfig?.ChatPosition === 'right'
                ? 'right bottom'
                : 'left bottom',
            ...(isOpen ? { opacity: 1, transform: 'scale(1)' } : {}),
          }}
          onTransitionEnd={() =>
            setTimeout(
              () => (!hasPromptedAI ? dialogRef.current?.show() : undefined),
              50
            )
          }
        >
          {chatType === 'WEB2TEXT' && (
            <>
              {aiChatConfig.EnableAIChat && (
                <AIPrompt
                  show={!hasPromptedAI}
                  color={chatBgColor}
                  onOptionClick={(option) => {
                    setChatType(option as typeof chatType);
                    setHasPromptedAI(true);
                  }}
                />
              )}
              <FloatingContainerBody
                isOpen={isOpen}
                toggleWeb2Text={toggleWeb2Text}
                showAIButton={aiChatConfig.EnableAIChat}
                promptAI={() => {
                  setChatType('WEB2TEXT');
                  setHasPromptedAI(false);
                }}
                recaptcha={recaptcha}
                fallbackStorePhone={fallbackStorePhone}
                chatBgColor={chatBgColor}
              />
            </>
          )}
          {chatType === 'AI' && aiChatConfig.EnableAIChat && (
            <Botpress
              showW2TButton={web2TextConfig.EnableWeb2Text}
              webchatClientId={aiChatConfig.WebchatClientId!}
              onConversationCreated={(conversationId) => {
                if (conversationId !== AIConversationId) {
                  setAIConversationId(conversationId);
                }
              }}
              isOpen={isOpen}
              config={aiChatConfig}
              color={{ chatBgColor: chatBgColor }}
              onActionItem={(action) => {
                if (
                  action === 'SWITCH_TO_WEB2TEXT' &&
                  web2TextConfig.EnableWeb2Text
                ) {
                  setChatType('WEB2TEXT');
                }
              }}
            />
          )}
        </div>
      </BotpressContext.Provider>
    </ErrorBoundary>
  );
};

export default FloatingOpenContainer;
